import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { User, Mail, Phone, MapPin, Save, Flame, CheckCircle2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', city: '', bio: '' });
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchLogs();
    fetchStats();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/profile`, { withCredentials: true });
      setProfile(data);
      setForm({ name: data.name || '', phone: data.phone || '', city: data.city || '', bio: data.bio || '' });
    } catch (err) { console.error(err); }
  };

  const fetchLogs = async () => {
    try {
      const { data } = await axios.get(`${API}/karma/logs`, { withCredentials: true });
      setLogs(data);
    } catch (err) { console.error(err); }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/karma/stats`, { withCredentials: true });
      setStats(data);
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, form, { withCredentials: true });
      toast.success('Profile updated!');
      await refreshUser();
      fetchProfile();
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#110A06]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="profile-page">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="mb-8">
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.2em] font-bold text-[#D4A54A] mb-1">Profile</motion.p>
          <motion.h1 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">Your Journey</motion.h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1 space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6 text-center">
              <div className="w-20 h-20 rounded-full bg-[#D4A54A]/10 flex items-center justify-center mx-auto mb-4 ring-2 ring-[#D4A54A]/20">
                {profile?.picture ? (
                  <img src={profile.picture} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="font-serif text-3xl font-semibold text-[#D4A54A]">{profile?.name?.[0]?.toUpperCase() || '?'}</span>
                )}
              </div>
              <h2 className="font-serif text-xl font-semibold text-[#F5E6C8]">{profile?.name}</h2>
              <p className="font-sans text-sm text-[#A89070] mt-1">{profile?.email}</p>
              {profile?.city && <p className="font-sans text-xs text-[#A89070] mt-1 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> {profile.city}</p>}
              {profile?.subscription ? (
                <div className="mt-4 inline-block bg-[#D4A54A]/10 text-[#D4A54A] px-3 py-1 rounded-full font-sans text-xs font-bold uppercase tracking-wider">
                  {profile.subscription.tier.replace('_', ' ')}
                </div>
              ) : (
                <p className="mt-4 font-sans text-xs text-[#A89070]">No active subscription</p>
              )}
            </motion.div>

            {/* Quick Stats */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D4A54A]/10 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-[#D4A54A]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-sans text-xs text-[#A89070] uppercase tracking-widest font-bold">Total Karma</p>
                  <p className="font-serif text-2xl font-semibold text-[#D4A54A]">{stats?.total_points || 0}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#7D9B6A]/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#7D9B6A]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-sans text-xs text-[#A89070] uppercase tracking-widest font-bold">Activities</p>
                  <p className="font-serif text-2xl font-semibold text-[#F5E6C8]">{stats?.total_activities || 0}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Edit Form + Activity History */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
              <h2 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-5">Edit Profile</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 flex items-center gap-1.5"><User className="w-3 h-3" /> Name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 focus:border-[#D4A54A] placeholder:text-[#5C4830]" />
                  </div>
                  <div>
                    <Label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 focus:border-[#D4A54A] placeholder:text-[#5C4830]" placeholder="+91 9876543210" />
                  </div>
                </div>
                <div>
                  <Label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> City</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="profile-city" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 focus:border-[#D4A54A] placeholder:text-[#5C4830]" placeholder="Mumbai, Delhi, Bangalore..." />
                </div>
                <div>
                  <Label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5">Bio</Label>
                  <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="profile-bio" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] placeholder:text-[#5C4830] min-h-[80px]" placeholder="Tell us about your karma journey..." />
                </div>
                <button onClick={handleSave} disabled={saving} data-testid="save-profile-btn" className="bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-6 py-2.5 font-sans text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4A54A]/20 disabled:opacity-50 flex items-center gap-2">
                  <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </motion.div>

            {/* Full Activity History */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
              <h2 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#D4A54A]" strokeWidth={1.5} /> Activity History
              </h2>
              {logs.length === 0 ? (
                <p className="font-sans text-sm text-[#A89070] py-6 text-center">No activities logged yet</p>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, i) => (
                    <div key={log.log_id || i} className="flex items-center gap-4 p-3 rounded-xl hover:bg-[#23180E] transition-colors">
                      <div className="w-9 h-9 rounded-lg bg-[#7D9B6A]/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-[#7D9B6A]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-sm font-medium text-[#F5E6C8]">{log.activity_title}</p>
                        {log.notes && <p className="font-sans text-xs text-[#A89070] mt-0.5 truncate">{log.notes}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-sans text-sm font-semibold text-[#D4A54A]">+{log.points}</p>
                        <p className="font-sans text-xs text-[#A89070]">{new Date(log.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
